import React from "react";
import { useOutletContext } from "react-router-dom";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { breadcrumbSchema } from "../data/seo";
import { IMAGES, SERVICES } from "../data/site";

const getBannerObjectPosition = (focusValue) => {
  const raw = typeof focusValue === "string" ? focusValue.trim().toLowerCase() : "";
  if (!raw || raw === "center") return "50% 50%";
  if (raw === "top") return "50% 0%";
  if (raw === "bottom") return "50% 100%";

  const offsetMatch = raw.match(/^(top|bottom)[-:\s]?(\d{1,3})$/);
  if (offsetMatch) {
    const direction = offsetMatch[1];
    const amount = Math.max(0, Math.min(100, Number(offsetMatch[2])));
    const yPos = direction === "top" ? amount : 100 - amount;
    return `50% ${yPos}%`;
  }

  const pairPercentMatch = raw.match(/^(\d{1,3})%\s+(\d{1,3})%$/);
  if (pairPercentMatch) {
    const xPos = Math.max(0, Math.min(100, Number(pairPercentMatch[1])));
    const yPos = Math.max(0, Math.min(100, Number(pairPercentMatch[2])));
    return `${xPos}% ${yPos}%`;
  }

  const singlePercentMatch = raw.match(/^(\d{1,3})%$/);
  if (singlePercentMatch) {
    const yPos = Math.max(0, Math.min(100, Number(singlePercentMatch[1])));
    return `50% ${yPos}%`;
  }

  return "50% 50%";
};

const includedProcedures = [
  "Treatment for dental pain, sensitivity, or infection",
  "Repair of chipped, cracked, or broken teeth",
  "Tooth-colored fillings and cosmetic bonding",
  "Repair or replacement of crowns, veneers, bridges, or fillings",
  "Porcelain crowns and veneers",
  "Dental implant treatment",
  "Professional teeth whitening",
  "Safe amalgam removal",
  "Biomimetic and minimally invasive restorations",
  "Professional cleaning and preventive care",
  "Multiple compatible procedures completed during one extended visit",
  "Temporary stabilization when final treatment requires healing or laboratory time",
];

const idealForList = [
  "Departing for a vacation or business trip within the next few days",
  "Preparing for international or extended travel",
  "Attending a wedding, destination event, or important celebration",
  "Experiencing pain, swelling, sensitivity, or discomfort",
  "Concerned about a damaged tooth or broken restoration",
  "Traveling to see Dr. K from another city or state",
  "Managing a demanding professional schedule",
  "Hoping to complete multiple treatments in fewer visits",
  "Seeking to avoid appointments with several different providers",
];

const planningSteps = [
  {
    title: "01 - Priority Contact",
    body: "Tell our team about your concerns, symptoms, departure date, upcoming event, and available timeframe.",
  },
  {
    title: "02 - Comprehensive Evaluation",
    body: "Dr. K examines your oral health and completes the diagnostic imaging needed to determine the safest and most effective treatment options.",
  },
  {
    title: "03 - Coordinated Treatment Plan",
    body: "Your care is organized into an efficient clinical sequence. We explain what can be completed during your reserved appointment and what may require healing, laboratory work, or follow-up.",
  },
  {
    title: "04 - Pre-Visit Preparation",
    body: "Necessary records, materials, prescriptions, laboratory work, and comfort arrangements are coordinated in advance whenever possible.",
  },
  {
    title: "05 - Reserved VIP Appointment",
    body: "An entire morning, afternoon, or full day is dedicated to your care, allowing Dr. K and her team to work carefully without rushing your treatment.",
  },
  {
    title: "06 - Personalized Follow-Up",
    body: "You receive clear aftercare instructions and a coordinated plan for any additional treatment that may be required.",
  },
];

const careFormats = [
  {
    title: "Immediate Pre-Travel Care",
    body: "For patients leaving within days, our first priority is diagnosing and treating - or safely stabilizing - any condition that could cause pain, infection, or complications while away.",
  },
  {
    title: "Half-Day VIP Care",
    body: "An entire morning or afternoon may be reserved when several compatible procedures can be completed during one extended appointment.",
  },
  {
    title: "Full-Day VIP Care",
    body: "For more comprehensive needs, a full day may be devoted to completing multiple procedures in a carefully planned sequence.",
  },
  {
    title: "Phased VIP Care",
    body: "Some treatments require healing, laboratory fabrication, or follow-up. In these cases, we address what is most urgent and organize the remaining care into the fewest clinically appropriate visits.",
  },
];

export const VipDentistryPage = () => {
  const { openBooking } = useOutletContext();
  const vipService = SERVICES.find(
    (service) => service.slug === "vip-dentistry",
  );
  const heroImage = vipService?.image || IMAGES.clinicInterior1;
  const bannerFocusRaw = vipService?.bannerfocus || vipService?.bannerFocus || "center";
  const bannerObjectPosition = getBannerObjectPosition(bannerFocusRaw);
  const singleLineTitleClass =
    "whitespace-nowrap text-[clamp(0.75rem,2.85vw,3.55rem)] leading-[1.1]";
  const singleLineSubTitleClass =
    "whitespace-nowrap text-[clamp(0.8rem,2.1vw,1.875rem)] leading-[1.15]";

  return (
    <div data-testid="vip-dentistry-page">
      <Seo
        title="VIP Dentistry | Smiles by Dr. K"
        description="Immediate, carefully coordinated VIP dental care designed to accomplish more in fewer visits when time is limited."
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: "VIP Dentistry", path: "/services/vip-dentistry" },
          ]),
        ]}
      />

      <section className="relative min-h-[40vh]  overflow-hidden flex items-end">
        <img
          src={heroImage}
          alt="VIP Dentistry private treatment setting"
          // className="absolute inset-0 w-full h-full object-cover"
    className={`absolute inset-0 w-full h-full object-cover transition-opacity  ease-in-out`}

          style={{ objectPosition: bannerObjectPosition }}
        />
      </section>

      <section className="py-10">
        <div className="container-x ">
          <div className="max-w-5xl">
            <p className="label-eyebrow text-gold">VIP DENTISTRY</p>
            <h1
              className={`heading-display text-ink max-w-5xl mt-4 ${singleLineTitleClass}`}
            >
              Immediate attention. More accomplished in fewer visits.
            </h1>
            <div className="max-w-4xl text-ink/80 leading-[1.85] space-y-6 mt-8">
              <p>
                Leaving for a vacation or business trip? Preparing for a wedding
                or important event? VIP Dentistry allows us to reserve an entire
                morning, afternoon, or full day for your care so we can address
                urgent concerns and complete as much treatment as safely
                possible within your available time.
              </p>
              <button type="button" onClick={openBooking} className="btn-outline inline-flex mt-4">
                REQUEST PRIORITY VIP CARE
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10  bg-cream/40">
        <div className="container-x ">
          <div className="max-w-5xl">
            <SectionHeading
              title="What can be accomplished during a VIP visit?"
              titleClassName={singleLineTitleClass}
            />
            <p className="text-ink/80 leading-[1.85] mt-6">
              Depending on your needs and clinical evaluation, your reserved
              appointment may include any combination of the following
              procedures:
            </p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 text-ink/80 leading-relaxed">
              {includedProcedures.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="text-gold">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-ink/80 leading-[1.85] mt-8">
              Some procedures may be completed in a single day, while others,
              such as certain implant treatments, may require healing and an
              additional visit. After your evaluation, Dr. K will explain
              exactly what can be accomplished within your timeframe.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-x">
          <div className="max-w-5xl text-ink/80 leading-[1.85] space-y-6">
            <SectionHeading
              title="Dental concerns before an upcoming event, meeting, or a trip?"
              titleClassName={singleLineTitleClass}
            />
            <p>
              A toothache, broken restoration, damaged tooth, or unexpected
              dental problem can place an important trip in jeopardy. Even a
              minor concern can become much more difficult to manage once you
              are away from home.
            </p>
            <p>
              VIP Dentistry provides an accelerated, carefully coordinated
              approach for patients who need immediate attention. We evaluate
              the problem, determine what should be addressed before travel, and
              reserve the appropriate amount of time to complete treatment
              whenever possible.
            </p>
            <p>
              Our goal is to help you leave feeling comfortable, prepared, and
              confident about your dental health.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-ink text-bone ">
        <div className="container-x">
          <SectionHeading
            eyebrow="Care Formats"
            title="One morning. One afternoon."
            italicWord="Or one carefully planned day."
            light
            titleClassName={singleLineTitleClass}
          />
          <p className="text-white/80 leading-[1.85] max-w-4xl mt-8">
            VIP Dentistry is designed for patients who need to accomplish more
            within a limited amount of time.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/20">
            {careFormats.map((format) => (
              <article key={format.title} className="bg-ink/60 p-8">
                <h3
                  className={`font-serif font-light text-white ${singleLineSubTitleClass}`}
                >
                  {format.title}
                </h3>
                <p className="mt-4 leading-[1.8] text-white/80">
                  {format.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-x">
          <div className="max-w-5xl">
            <SectionHeading
              title="Who is VIP Dentistry for?"
              titleClassName={singleLineTitleClass}
            />
            <p className="text-ink/80 leading-[1.85] mt-6">
              VIP Dentistry may be ideal if you are:
            </p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 text-ink/80 leading-relaxed">
              {idealForList.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="text-gold">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-ink/80 leading-[1.85] mt-8">
              You do not need to be a celebrity to receive VIP care. You simply
              need a dental experience designed around the realities of your
              time and circumstances.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-cream/50">
        <div className="container-x">
          <SectionHeading
            title="How your VIP visit is planned"
            titleClassName={singleLineTitleClass}
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10">
            {planningSteps.map((step) => (
              <article key={step.title} className="bg-white p-8 md:p-10">
                <h3
                  className={`font-serif font-light text-ink ${singleLineSubTitleClass}`}
                >
                  {step.title}
                </h3>
                <p className="mt-4 text-ink/75 leading-[1.8]">{step.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-x">
          <div className="max-w-5xl text-ink/80 leading-[1.85] space-y-6">
            <SectionHeading
              title="What about dental implants?"
              titleClassName={singleLineTitleClass}
            />
            <p>
              Some implant procedures may allow an implant, and in selected
              cases, a temporary tooth, to be placed during the same visit. This
              depends on the location of the implant, available bone, bite,
              implant type, and overall clinical condition.
            </p>
            <p>
              A final implant crown often requires appropriate healing and may
              need to be completed later. Same-day implant treatment can only be
              considered after an examination and diagnostic imaging.
            </p>
            <p>
              If definitive treatment cannot be completed before your departure
              or event, we will discuss whether a safe temporary solution is
              appropriate.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-cream/40">
        <div className="container-x text-ink/80 leading-[1.85] space-y-6">
        <div className="max-w-5xl">
          <SectionHeading
            title="One doctor. One coordinated plan."
            titleClassName={singleLineTitleClass}
          />
          <p>
            When time is limited, being sent between several offices can create
            unnecessary delays.
          </p>
          <p>
            Whenever clinically appropriate, Dr. K coordinates your treatment
            through one comprehensive plan and provides as much of your care as
            possible within the practice. If another dental or medical
            specialist is necessary, we will explain why and help coordinate
            that care efficiently.
          </p>
          <p>
            The goal is not rushed dentistry. It is thoughtful, decisive care
            that eliminates unnecessary delays while protecting your health and
            the quality of your results.
          </p>
        </div>
        </div>
      </section>
      <section className="py-10">
        <div className="container-x text-ink/80 leading-[1.85] space-y-6">
          <div className="max-w-5xl">
            <SectionHeading
              title="Designed Around Your Schedule"
              titleClassName={singleLineTitleClass}
            />
            <p>
              Your time is valuable. For executives, business owners, and
              professionals with demanding schedules, we offer late evening
              appointments by arrangement, making it easier to receive
              exceptional dental care without disrupting your workday.
            </p>
            <p>
              Whether you&apos;re balancing board meetings, business travel, or a
              full calendar, our team will work with you to schedule
              appointments at times that fit your lifestyle, so you never have
              to choose between your career and your smile.
            </p>
          </div>
        </div>
      </section>
            
      <section className="py-10 bg-ink text-bone">
        <div className="container-x text-center">
          <div className="mx-auto max-w-5xl">
            <h2 className={`heading-display ${singleLineTitleClass}`}>
              Contact us as{" "}
              <span className="italic text-gold-light">soon as possible.</span>
            </h2>
            <p className="mt-6 text-white/80 leading-[1.8]">
              The earlier we evaluate your concern, the more treatment options
              may be available, especially if you are traveling or attending an
              important event soon.
            </p>
            <button type="button" onClick={openBooking} className="btn-outline-light inline-flex mt-10">
              REQUEST PRIORITY VIP CARE
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
